import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Edit,
  Plus,
  MoreHorizontal,
  Users,
  Loader2,
  Share2,
  Copy,
  Check,
  MessageCircle,
  Linkedin,
  Mail,
} from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import {
  Card,
  CardContent,
} from "@/shared/components/ui/card";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";

import { TabsContent } from "@/shared/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import type { RootState } from "@/store/store";
import { getTeamMembers } from "@/modules/team/api/team.api";
import { updateJob, createJobShareLink, } from "@/modules/jobs/api/job.api";

import { DEPARTMENTS } from "@/shared/constants/department";

import CreateJobDialog from "../../../jobs/components/CreateJobDialog";

import { useDashboardJobs } from "@/modules/jobs/hooks/useDashboardJobs";

import { formatDate } from "@/modules/hrPortal/utils/formatters";

import type {
  Job,
  JobStatus,
  JobVisibility,
} from "@/modules/jobs/types/job";

// =========================================================
// FILTER TYPES
// =========================================================

type JobStatusFilter =
  | "ALL"
  | JobStatus;

type JobVisibilityFilter =
  | "ALL"
  | JobVisibility;

// =========================================================
// CONSTANTS
// =========================================================

const ALL_DEPARTMENTS = "ALL_DEPARTMENTS";
const ALL_STATUS = "ALL";
const ALL_VISIBILITY = "ALL";

// =========================================================
// JOBS TAB
// =========================================================

export default function JobsTab() {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const isHRAdmin = user?.role?.trim().toUpperCase() === "HR";

  // -------------------------------------------------------
  // Fetch Jobs
  // -------------------------------------------------------

  const {
    jobs,
    loading: jobsLoading,
    error: jobsError,
  } = useDashboardJobs({
    page: 1,
    limit: 100,
  });

  // -------------------------------------------------------
  // Dialog State
  // -------------------------------------------------------

  const [showDialog, setShowDialog] = useState(false);

  const [editingJob, setEditingJob] =
    useState<Job | null>(null);

  const [assignmentJob, setAssignmentJob] = useState<Job | null>(null);
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [assignmentError, setAssignmentError] = useState("");
  const [assignmentSuccess, setAssignmentSuccess] = useState("");
  const [sharingJob, setSharingJob] = useState<Job | null>(null);
  const [shareLink, setShareLink] = useState("");
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState("");
  const [copied, setCopied] = useState(false);

  // -------------------------------------------------------
  // Filter State
  // -------------------------------------------------------

  const [statusFilter, setStatusFilter] =
    useState<JobStatusFilter>(ALL_STATUS);

  const [visibilityFilter, setVisibilityFilter] =
    useState<JobVisibilityFilter>(ALL_VISIBILITY);

  const [departmentFilter, setDepartmentFilter] =
    useState<string>(ALL_DEPARTMENTS);

  // -------------------------------------------------------
  // Pagination
  // -------------------------------------------------------

  const [currentPage, setCurrentPage] = useState(1);

  const jobsPerPage = 6;

  // =======================================================
  // STATUS + VISIBILITY FILTER
  // =======================================================

  const filteredJobs = useMemo(() => {
    const allJobs = (jobs || []) as Job[];

    return allJobs.filter((job) => {
      const matchesStatus =
        statusFilter === ALL_STATUS ||
        job.status === statusFilter;

      const matchesVisibility =
        visibilityFilter === ALL_VISIBILITY ||
        job.visibility === visibilityFilter;

      return (
        matchesStatus &&
        matchesVisibility
      );
    });
  }, [
    jobs,
    statusFilter,
    visibilityFilter,
  ]);

  // =======================================================
  // DEPARTMENT FILTER
  // =======================================================

  const departmentFilteredJobs =
    useMemo(() => {
      if (
        departmentFilter ===
        ALL_DEPARTMENTS
      ) {
        return filteredJobs;
      }

      return filteredJobs.filter(
        (job) =>
          job.department ===
          departmentFilter
      );
    }, [
      filteredJobs,
      departmentFilter,
    ]);

  // =======================================================
  // PAGINATION
  // =======================================================

  const totalPages = Math.max(
    1,
    Math.ceil(
      departmentFilteredJobs.length /
      jobsPerPage
    )
  );

  const paginatedJobs = useMemo(() => {
    const startIndex =
      (currentPage - 1) *
      jobsPerPage;

    return departmentFilteredJobs.slice(
      startIndex,
      startIndex + jobsPerPage
    );
  }, [
    departmentFilteredJobs,
    currentPage,
  ]);

  // =======================================================
  // RESET PAGE WHEN FILTER CHANGES
  // =======================================================

  useEffect(() => {
    setCurrentPage(1);
  }, [
    statusFilter,
    visibilityFilter,
    departmentFilter,
  ]);

  // =======================================================
  // KEEP PAGE VALID
  // =======================================================

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [
    currentPage,
    totalPages,
  ]);

  // =======================================================
  // JOB TEAM ASSIGNMENT (HR ADMIN ONLY)
  // =======================================================

  const openAssignmentDialog = async (job: Job) => {
    if (!isHRAdmin) return;

    setAssignmentJob(job);
    setAssignmentError("");
    setAssignmentSuccess("");
    setLoadingMembers(true);

    const existingAssignments = (job as any).teamMembers
      ?? (job as any).assignedTeamMembers
      ?? (job as any).assignedMembers
      ?? [];
    const existingIds = Array.isArray(existingAssignments)
      ? existingAssignments.map((member: any) =>
        typeof member === "string" ? member : member?.id ?? member?.teamMemberId
      ).filter(Boolean)
      : [];
    const assignedToIds = (job as any).assignedToIds;
    setSelectedMemberIds(
      Array.isArray(assignedToIds) ? assignedToIds : existingIds
    );

    try {
      const response: any = await getTeamMembers({ status: "active" });
      const possibleLists = [
        response?.data?.teamMembers,
        response?.data?.members,
        response?.data?.rows,
        response?.teamMembers,
        response?.members,
        response?.rows,
        response?.data,
        response,
      ];
      const members = possibleLists.find(Array.isArray) ?? [];
      setTeamMembers(members.filter((member: any) =>
        member && member.id && member.status?.toLowerCase() !== "inactive"
      ));
    } catch (error: any) {
      setAssignmentError(
        error?.response?.data?.message || "Unable to load your team members."
      );
    } finally {
      setLoadingMembers(false);
    }
  };

  const toggleMemberSelection = (memberId: string) => {
    setSelectedMemberIds((current) =>
      current.includes(memberId)
        ? current.filter((id) => id !== memberId)
        : [...current, memberId]
    );
  };

  const saveJobTeamAssignment = async () => {
    if (!assignmentJob || !isHRAdmin) return;

    setSavingAssignment(true);
    setAssignmentError("");
    setAssignmentSuccess("");

    try {
      // The existing jobs API accepts job updates. Send the selected member IDs
      // using the backend's assignedToIds field.
      await updateJob(assignmentJob.id, { assignedToIds: selectedMemberIds });
      setAssignmentSuccess("Job team assignment saved successfully.");
      setAssignmentJob(null);
    } catch (error: any) {
      setAssignmentError(
        error?.response?.data?.message || "Unable to save job team assignment."
      );
    } finally {
      setSavingAssignment(false);
    }
  };

  const isShareEligible = (job: Job) =>
    job.status === "PUBLISHED" &&
    job.visibility === "PUBLIC";

  const openShareDialog = async (job: Job) => {
    if (!isShareEligible(job)) return;

    setSharingJob(job);
    setShareLink("");
    setShareError("");
    setCopied(false);
    setShareLoading(true);

    try {
      const response: any = await createJobShareLink(job.id);

      if (!response?.sharePath) {
        throw new Error("Share link was not returned by the server.");
      }

      const generatedUrl = new URL(
        response.sharePath,
        window.location.origin
      );

      setShareLink(generatedUrl.toString());
    } catch (error: any) {
      setShareError(
        error?.response?.data?.message ||
        error?.message ||
        "Unable to generate share link."
      );
    } finally {
      setShareLoading(false);
    }
  };

  const getChannelShareUrl = (medium: string) => {
    if (!shareLink) return "";

    const url = new URL(shareLink);
    url.searchParams.set("utm_medium", medium);

    return url.toString();
  };

  const copyShareLink = async () => {
    if (!shareLink) return;

    try {
      await navigator.clipboard.writeText(
        getChannelShareUrl("link")
      );

      setCopied(true);

      window.setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch {
      setShareError("Unable to copy link. Please copy it manually.");
    }
  };

  const shareOnWhatsApp = () => {
    if (!shareLink || !sharingJob) return;

    const url = getChannelShareUrl("whatsapp");

    const message = `Check out this job opportunity: ${sharingJob.title}\n${url}`;

    window.open(
      `https://wa.me/?text=${encodeURIComponent(message)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const shareOnLinkedIn = () => {
    if (!shareLink) return;

    const url = getChannelShareUrl("linkedin");

    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const shareViaEmail = () => {
    if (!shareLink || !sharingJob) return;

    const url = getChannelShareUrl("email");

    const subject = `Job Opportunity: ${sharingJob.title}`;
    const body = `Please check out this job opportunity:\n\n${sharingJob.title}\n${url}`;

    window.location.href =
      `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  // =======================================================
  // FILTER HANDLERS
  // =======================================================

  const handleStatusFilterChange = (
    value: string
  ) => {
    setStatusFilter(
      value as JobStatusFilter
    );

    setCurrentPage(1);
  };

  const handleVisibilityFilterChange = (
    value: string
  ) => {
    setVisibilityFilter(
      value as JobVisibilityFilter
    );

    setCurrentPage(1);
  };

  const handleDepartmentFilterChange = (
    value: string
  ) => {
    setDepartmentFilter(value);
    setCurrentPage(1);
  };

  // =======================================================
  // RENDER
  // =======================================================

  return (
    <TabsContent
      value="jobs"
      className="space-y-6 sm:space-y-8"
    >
      {/* =================================================
          FILTERS + CREATE JOB
          ================================================= */}

      <div className="flex justify-end">
        <div
          className="
            grid w-full grid-cols-1 gap-2
            sm:grid-cols-2
            lg:grid-cols-4
            xl:w-auto
            xl:flex
            xl:flex-wrap
            xl:justify-end
          "
        >
          {/* Job Status */}
          <Select
            value={statusFilter}
            onValueChange={
              handleStatusFilterChange
            }
          >
            <SelectTrigger
              className="
                h-10 w-full bg-white
                sm:min-w-0
                lg:w-full
                xl:w-[175px]
              "
            >
              <SelectValue placeholder="All Job Statuses" />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value={ALL_STATUS}>
                All Job Statuses
              </SelectItem>

              <SelectItem value="DRAFT">
                Draft
              </SelectItem>

              <SelectItem value="PUBLISHED">
                Published
              </SelectItem>

              <SelectItem value="CLOSED">
                Closed
              </SelectItem>

              <SelectItem value="ARCHIVED">
                Archived
              </SelectItem>
            </SelectContent>
          </Select>

          {/* Visibility */}
          <Select
            value={visibilityFilter}
            onValueChange={
              handleVisibilityFilterChange
            }
          >
            <SelectTrigger
              className="
                h-10 w-full bg-white
                sm:min-w-0
                lg:w-full
                xl:w-[175px]
              "
            >
              <SelectValue placeholder="All Visibility" />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value={ALL_VISIBILITY}>
                All Visibility
              </SelectItem>

              <SelectItem value="PUBLIC">
                Public
              </SelectItem>

              <SelectItem value="INTERNAL">
                Internal
              </SelectItem>
            </SelectContent>
          </Select>

          {/* Department */}
          <Select
            value={departmentFilter}
            onValueChange={
              handleDepartmentFilterChange
            }
          >
            <SelectTrigger
              className="
                h-10 w-full bg-white
                sm:min-w-0
                lg:w-full
                xl:w-[190px]
              "
            >
              <SelectValue placeholder="All Departments" />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value={ALL_DEPARTMENTS}>
                All Departments
              </SelectItem>

              {DEPARTMENTS.map(
                (department) => (
                  <SelectItem
                    key={department}
                    value={department}
                  >
                    {department}
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>

          {/* Create Job */}
          <Button
            type="button"
            className="
              h-10 w-full
              sm:w-full
              lg:w-full
              xl:w-auto
              xl:shrink-0
            "
            onClick={() => {
              setEditingJob(null);
              setShowDialog(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Create Job
          </Button>
        </div>
      </div>

      {/* =================================================
          API ERROR
          ================================================= */}

      {jobsError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {jobsError}
        </div>
      )}

      {/* =================================================
          JOBS
          ================================================= */}

      {jobsLoading ? (
        <div className="rounded-xl border bg-white px-6 py-14 text-center">
          Loading jobs...
        </div>
      ) : departmentFilteredJobs.length === 0 ? (
        <div className="rounded-xl border border-dashed bg-white px-6 py-10 text-center">
          No jobs found.
        </div>
      ) : (
        <div>
          {/* Results Summary */}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-medium text-gray-700">
              Showing{" "}
              {departmentFilteredJobs.length === 0
                ? 0
                : (currentPage - 1) *
                jobsPerPage +
                1}{" "}
              -{" "}
              {Math.min(
                currentPage * jobsPerPage,
                departmentFilteredJobs.length
              )}{" "}
              of{" "}
              {departmentFilteredJobs.length}{" "}
              {departmentFilteredJobs.length === 1
                ? "job"
                : "jobs"}
            </p>
          </div>

          {/* Job Cards */}
          <div
            className="
              grid grid-cols-1 gap-4
              sm:gap-5
              md:grid-cols-2
              xl:grid-cols-3
            "
          >
            {paginatedJobs.map((job) => (
              <Card
                key={job.id}
                className="
                  border-gray-200
                  bg-white
                  shadow-sm
                  transition-shadow
                  hover:shadow-md
                "
              >
                <CardContent className="flex min-h-[210px] flex-col p-4 sm:p-5">
                  {/* Header */}
                  <div className="flex min-w-0 items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="line-clamp-2 text-base font-semibold text-gray-900 sm:text-lg">
                        {job.title}
                      </h3>

                      <p className="mt-1 text-sm text-gray-500">
                        {job.department}
                      </p>

                      {job.jobCode && (
                        <p className="mt-1 text-xs font-medium text-gray-500">
                          Job ID:{" "}
                          {job.jobCode.replace(
                            /^JOB-/,
                            ""
                          )}
                        </p>
                      )}
                    </div>

                    <span
                      className="
                        shrink-0 rounded-full
                        bg-slate-100 px-2.5 py-1
                        text-[11px] font-semibold
                        text-slate-700
                        sm:px-3 sm:text-xs
                      "
                    >
                      {job.applicantCount ?? 0} applicants
                    </span>
                  </div>

                  {/* Job Meta */}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                      {job.status}
                    </span>

                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                      {job.visibility}
                    </span>
                  </div>

                  {/* Job Activity */}
                  <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-500">
                    {job.updatedAt && job.createdAt && job.updatedAt !== job.createdAt ? (
                      <>
                        <span>{formatDate(job.updatedAt)}</span>
                        <span className="text-gray-300">·</span>
                        <span>
                          Updated by{" "}
                          <span className="font-medium text-gray-700">
                            {job.updater?.name || "—"}
                          </span>
                        </span>
                      </>
                    ) : (
                      <>
                        <span>
                          {job.createdAt ? formatDate(job.createdAt) : "Recently added"}
                        </span>
                        <span className="text-gray-300">·</span>
                        <span>
                          Created by{" "}
                          <span className="font-medium text-gray-700">
                            {job.creator?.name || "—"}
                          </span>
                        </span>
                      </>
                    )}
                  </div>


                  {/* Actions */}
                  <div className="mt-auto flex items-center gap-2 pt-5">

                    {/* View Applications - Smaller Button */}
                    <Button
                      type="button"
                      variant="outline"
                      className="h-9 min-w-0 flex-1 px-2 text-xs sm:px-3 sm:text-sm"
                      onClick={() =>
                        navigate(`/hr/jobs/${job.id}/applicants`)
                      }
                    >
                      <span className="truncate whitespace-nowrap">
                        View Applications
                      </span>
                    </Button>

                    {/* Share Job */}
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 shrink-0"
                      aria-label="Share job"
                      title={
                        isShareEligible(job)
                          ? "Share job"
                          : "Only published public jobs can be shared"
                      }
                      disabled={!isShareEligible(job)}
                      onClick={() => void openShareDialog(job)}
                    >
                      <Share2 className="h-4 w-4" />
                    </Button>

                    {/* Edit Job */}
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 shrink-0"
                      aria-label="Edit job"
                      onClick={() => {
                        setEditingJob(job);
                        setShowDialog(true);
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>

                    {/* Assign Team Members */}
                    {isHRAdmin && (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-9 w-9 shrink-0"
                        aria-label="Assign team members to job"
                        title="Assign team members"
                        onClick={() => void openAssignmentDialog(job)}
                      >
                        <MoreHorizontal className="h-5 w-5" />
                      </Button>
                    )}

                  </div>

                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              className="
                mt-6 flex flex-wrap
                items-center justify-center
                gap-2
              "
            >
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={currentPage === 1}
                onClick={() =>
                  setCurrentPage((page) =>
                    Math.max(1, page - 1)
                  )
                }
              >
                Previous
              </Button>

              {Array.from(
                { length: totalPages },
                (_, index) => index + 1
              ).map((page) => (
                <Button
                  key={page}
                  type="button"
                  variant={
                    currentPage === page
                      ? "default"
                      : "outline"
                  }
                  size="sm"
                  className="min-w-9"
                  onClick={() =>
                    setCurrentPage(page)
                  }
                >
                  {page}
                </Button>
              ))}

              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={
                  currentPage === totalPages
                }
                onClick={() =>
                  setCurrentPage((page) =>
                    Math.min(
                      totalPages,
                      page + 1
                    )
                  )
                }
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}

      {/* =================================================
          CREATE / EDIT JOB DIALOG
          ================================================= */}

      <CreateJobDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        editingJob={editingJob}
      />

      <Dialog
        open={Boolean(assignmentJob)}
        onOpenChange={(open) => {
          if (!open && !savingAssignment) {
            setAssignmentJob(null);
            setAssignmentError("");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Team Members</DialogTitle>
            <DialogDescription>
              Select members from your HR team for {assignmentJob?.title ?? "this job"}.
            </DialogDescription>
          </DialogHeader>

          {assignmentError && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {assignmentError}
            </p>
          )}

          <div className="max-h-72 space-y-2 overflow-y-auto py-1">
            {loadingMembers ? (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading team members...
              </div>
            ) : teamMembers.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500">
                No active team members found in your team.
              </p>
            ) : (
              teamMembers.map((member: any) => {
                const checked = selectedMemberIds.includes(member.id);
                return (
                  <button
                    key={member.id}
                    type="button"
                    onClick={() => toggleMemberSelection(member.id)}
                    className={`flex w-full items-center gap-3 rounded-lg border px-3 py-3 text-left transition-colors ${checked
                      ? "border-blue-300 bg-blue-50"
                      : "border-gray-200 bg-white hover:bg-gray-50"
                      }`}
                  >
                    <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border text-xs ${checked ? "border-blue-600 bg-blue-600 text-white" : "border-gray-300"
                      }`}>
                      {checked ? "✓" : ""}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-gray-900">
                        {member.name || member.fullName || member.email}
                      </span>
                      {member.email && (
                        <span className="block truncate text-xs text-gray-500">{member.email}</span>
                      )}
                    </span>
                    <Users className="h-4 w-4 text-gray-400" />
                  </button>
                );
              })
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={savingAssignment}
              onClick={() => setAssignmentJob(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={loadingMembers || savingAssignment || teamMembers.length === 0}
              onClick={() => void saveJobTeamAssignment()}
            >
              {savingAssignment && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Assignment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SHARE JOB DIALOG */}
      <Dialog
        open={Boolean(sharingJob)}
        onOpenChange={(open) => {
          if (!open && !shareLoading) {
            setSharingJob(null);
            setShareLink("");
            setShareError("");
            setCopied(false);
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Share Job</DialogTitle>
            <DialogDescription>
              Share this job opportunity using your tracked referral link.
            </DialogDescription>
          </DialogHeader>

          {sharingJob && (
            <div className="space-y-4">
              <div className="rounded-lg border bg-slate-50 p-4">
                <h3 className="font-semibold text-gray-900">
                  {sharingJob.title}
                </h3>

                <p className="mt-1 text-sm text-gray-500">
                  {sharingJob.department || "N/A"}
                </p>

                {sharingJob.jobCode && (
                  <p className="mt-1 text-xs text-gray-500">
                    Job ID: {sharingJob.jobCode}
                  </p>
                )}
              </div>

              {shareError && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {shareError}
                </div>
              )}

              {shareLoading ? (
                <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-500">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Generating tracked link...
                </div>
              ) : shareLink ? (
                <>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Tracked Referral Link
                    </label>

                    <div className="flex gap-2">
                      <input
                        type="text"
                        readOnly
                        value={getChannelShareUrl("link")}
                        className="h-10 min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-3 text-sm outline-none"
                        onFocus={(event) =>
                          event.currentTarget.select()
                        }
                      />

                      <Button
                        type="button"
                        onClick={() => void copyShareLink()}
                        className="shrink-0"
                      >
                        {copied ? (
                          <Check className="mr-2 h-4 w-4" />
                        ) : (
                          <Copy className="mr-2 h-4 w-4" />
                        )}
                        {copied ? "Copied" : "Copy Link"}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <p className="text-sm font-medium text-gray-700">
                      Share via
                    </p>

                    <div className="grid grid-cols-3 gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        className="h-auto flex-col gap-2 py-3"
                        onClick={shareOnWhatsApp}
                      >
                        <MessageCircle className="h-5 w-5 text-green-600" />
                        <span className="text-xs">WhatsApp</span>
                      </Button>

                      <Button
                        type="button"
                        variant="outline"
                        className="h-auto flex-col gap-2 py-3"
                        onClick={shareOnLinkedIn}
                      >
                        <Linkedin className="h-5 w-5 text-blue-700" />
                        <span className="text-xs">LinkedIn</span>
                      </Button>

                      <Button
                        type="button"
                        variant="outline"
                        className="h-auto flex-col gap-2 py-3"
                        onClick={shareViaEmail}
                      >
                        <Mail className="h-5 w-5 text-gray-600" />
                        <span className="text-xs">Email</span>
                      </Button>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={shareLoading}
              onClick={() => {
                setSharingJob(null);
                setShareLink("");
                setShareError("");
                setCopied(false);
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </TabsContent>
  );
}